import { Modal, Typography } from "antd";
import {
  ArrowRightOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import type { DiffEntry } from "./socketToAssetMapUtils";

type SaveConfirmationModalProps = {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  confirmLoading: boolean;
  diff: DiffEntry[];
};

export function SaveConfirmationModal({
  open,
  onOk,
  onCancel,
  confirmLoading,
  diff,
}: SaveConfirmationModalProps) {
  return (
    <Modal
      title="Confirm Changes"
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okText="Save"
    >
      {diff.length === 0 ? (
        <Typography.Text>No changes to save.</Typography.Text>
      ) : (
        <div className="flex flex-col gap-3">
          <Typography.Text type="secondary" className="text-xs">
            {diff.length} {diff.length === 1 ? "socket" : "sockets"} affected
          </Typography.Text>
          {diff.map((entry) => {
            if (entry.kind === "added") {
              return (
                <div
                  key={entry.socketId}
                  className="rounded-lg border border-green-200 overflow-hidden"
                >
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border-b border-green-200">
                    <PlusCircleOutlined className="text-green-600 text-sm" />
                    <span className="font-semibold text-sm text-green-800">
                      {entry.socketId}
                    </span>
                    <span className="ml-auto text-[11px] font-medium text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                      New
                    </span>
                  </div>
                  {entry.assets.length > 0 && (
                    <div className="px-3 py-2 flex flex-col gap-1">
                      {entry.assets.map((asset, i) => (
                        <div
                          key={asset}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="font-mono text-[11px] text-gray-400 w-5 text-right">
                            #{i + 1}
                          </span>
                          <span className="text-gray-700">{asset}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (entry.kind === "removed") {
              return (
                <div
                  key={entry.socketId}
                  className="rounded-lg border border-red-200 overflow-hidden"
                >
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-b border-red-200">
                    <MinusCircleOutlined className="text-red-500 text-sm" />
                    <span className="font-semibold text-sm text-red-800 line-through">
                      {entry.socketId}
                    </span>
                    <span className="ml-auto text-[11px] font-medium text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                      Removed
                    </span>
                  </div>
                  {entry.assets.length > 0 && (
                    <div className="px-3 py-2 flex flex-col gap-1">
                      {entry.assets.map((asset, i) => (
                        <div
                          key={asset}
                          className="flex items-center gap-2 text-xs text-gray-400"
                        >
                          <span className="font-mono text-[11px] w-5 text-right">
                            #{i + 1}
                          </span>
                          <span className="line-through">{asset}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (entry.kind === "changed") {
              const maxLen = Math.max(
                entry.oldAssets.length,
                entry.newAssets.length,
              );
              return (
                <div
                  key={entry.socketId}
                  className="rounded-lg border border-gray-200 overflow-hidden"
                >
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <EditOutlined className="text-amber-500 text-sm" />
                    <span className="font-semibold text-sm text-gray-800">
                      {entry.socketId}
                    </span>
                    <span className="ml-auto text-[11px] font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                      Modified
                    </span>
                  </div>
                  <div className="px-3 py-2 flex flex-col gap-1">
                    {Array.from({ length: maxLen }, (_, i) => {
                      const oldAsset = entry.oldAssets[i];
                      const newAsset = entry.newAssets[i];
                      const unchanged = oldAsset === newAsset;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="font-mono text-[11px] text-gray-400 w-5 text-right">
                            #{i + 1}
                          </span>
                          {unchanged ? (
                            <span className="text-gray-400">{oldAsset}</span>
                          ) : !oldAsset ? (
                            <span className="text-green-600 font-medium">
                              + {newAsset}
                            </span>
                          ) : !newAsset ? (
                            <span className="text-red-500 line-through">
                              {oldAsset}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <span className="text-red-500 line-through">
                                {oldAsset}
                              </span>
                              <ArrowRightOutlined className="text-[10px] text-gray-300" />
                              <span className="text-green-600 font-medium">
                                {newAsset}
                              </span>
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
          })}
        </div>
      )}
    </Modal>
  );
}
